"use client";

import { navigationClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Link from "next/link";
import { useMemo } from "react";

/**
 * Footer 1034 — BDU Footer.
 *
 * Port of BDU v1 `components/organisms/Footer.tsx`: a background-image panel with
 * centred (mobile) / left-aligned (desktop) link columns, each heading carrying a
 * full-width white rule, then a divider and two centred legal lines.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here; the legal lines' `lg:` (1024px) has
 * no token in this app's scale and is written as `min-[1024px]:`.
 *
 * The reference's background art is a hardcoded local asset, so it stays hardcoded
 * here (copied to `public/images/bdu_footer_background.png`) rather than becoming a
 * CMS column — matching the reference's own treatment of it.
 *
 * Column widths in the reference switch at index 4: the first four columns are
 * `calc(25% - 1.5rem)` and any beyond that are `calc(25% - 1rem)`.
 */

const BACKGROUND_IMAGE = "/images/bdu_footer_background.png";

interface FooterLink {
  id?: number;
  label?: string;
  label_color?: string;
  url?: string;
  priority?: number;
  is_active?: boolean;
}

interface FooterCategory {
  id?: number;
  name?: string;
  name_text_color?: string;
  order_index?: number;
  status?: boolean;
  is_active?: boolean;
  items?: FooterLink[];
}

interface Footer1034Data {
  categories?: FooterCategory[];
  text_color?: string;
  enable_footer_content?: boolean;
  enable_copyright?: boolean;
  copyright_text?: string;
  copyright_text_color?: string;
  /** Disclaimer line above the copyright */
  title?: string;
  title_text_color?: string;
  /** Divider under the columns */
  gradient_start_color?: string;
  status?: boolean;
}

const Footer1034 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as Footer1034Data | undefined;

  const {
    text_color = "#FFFFFF",
    copyright_text = "Copyright 2026 of Bharathidasan University | All Rights Reserved",
    copyright_text_color = "#FFFFFF",
    title = "Disclaimer: bduedu.in is owned by bdu.ac.in",
    title_text_color = "#FFFFFF",
    gradient_start_color = "#5B789F",
  } = sectionData || {};

  const showContent = isCmsShowFlagOn(sectionData?.enable_footer_content, true);
  const showCopyright = isCmsShowFlagOn(sectionData?.enable_copyright, true);

  const categories = useMemo(() => {
    const cats = sectionData?.categories;
    if (!Array.isArray(cats)) return [];
    return cats
      .filter((c) => c?.status !== false && c?.is_active !== false)
      .sort(
        (a, b) => (Number(a?.order_index) || 0) - (Number(b?.order_index) || 0),
      );
  }, [sectionData?.categories]);

  if (!sectionData?.status) return null;

  return (
    <footer
      className={cn(
        "relative flex size-full flex-col items-center justify-center gap-8 p-[18px]",
        "bg-cover bg-center bg-no-repeat tab:gap-10 tab:px-12 tab:py-10",
      )}
      style={{ backgroundImage: `url("${BACKGROUND_IMAGE}")` }}
    >
      {showContent && categories.length > 0 && (
        <>
          <div className="flex w-full flex-col justify-center gap-y-8 tab:flex-row tab:flex-wrap tab:gap-x-8 tab:gap-y-10">
            {categories.map((category, index) => {
              const links = [...(category.items || [])]
                .filter((l) => l?.is_active !== false && !!l?.label)
                .sort(
                  (a, b) =>
                    (Number(a?.priority) || 0) - (Number(b?.priority) || 0),
                );

              return (
                <div
                  key={category.id ?? index}
                  className={cn(
                    "flex flex-col items-center gap-y-4 tab:items-start tab:gap-y-[14px]",
                    index >= 4
                      ? "tab:w-[calc(25%-1rem)]"
                      : "tab:w-[calc(25%-1.5rem)]",
                  )}
                >
                  <h4
                    className="relative mb-2 text-center text-xl/normal font-medium tab:text-left tab:text-2xl/normal"
                    style={{ color: category.name_text_color || text_color }}
                  >
                    {category.name}
                    {/* .footer-underline::after — full-width 2px rule, 8px below */}
                    <span
                      aria-hidden
                      className="absolute -bottom-2 left-1/2 h-[2px] w-full -translate-x-1/2"
                      style={{ backgroundColor: "#FFFFFF", opacity: 1 }}
                    />
                  </h4>

                  {links.map((link, i) => (
                    <Link
                      key={link.id ?? i}
                      href={link.url || "#"}
                      className="m-0 h-3 p-0 text-xs font-extralight underline-offset-4 hover:underline"
                      style={{ color: link.label_color || text_color }}
                      onClick={() =>
                        navigationClick({
                          section: "footer",
                          component: "footer_link",
                          label: link.label || "",
                          redirectLink: link.url || "",
                          parent: category.name || "",
                        })
                      }
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
          <div
            className="w-full border-b"
            style={{ borderColor: gradient_start_color }}
          />
        </>
      )}

      {title && (
        <p
          className="w-fit text-center text-xs font-normal min-[1024px]:text-base"
          style={{ color: title_text_color }}
        >
          {title}
        </p>
      )}

      {showCopyright && copyright_text && (
        <p
          className="w-fit text-center text-xs font-normal min-[1024px]:text-base"
          style={{ color: copyright_text_color }}
        >
          {copyright_text}{" "}
          <Link href="/privacy-policy" className="underline">
            privacy policy
          </Link>
        </p>
      )}
    </footer>
  );
};

export default Footer1034;
